import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { MoneyText } from './MoneyText.js';

/**
 * What a statement actually changed (docs/CAS-PARSER-ARCHITECTURE.md).
 *
 * A wealth app that silently rewrites your holdings is asking for trust it has
 * not earned. This shows the diff line by line — what was added, what moved,
 * and what vanished — so an import is reviewable rather than magical.
 *
 * The honesty here is structural, not cosmetic. The pipeline computes the
 * ChangeSet BEFORE any write and stores it verbatim on the document, then
 * applies it inside the same transaction. So this is not a re-derived summary
 * that could disagree with reality: it is the same object that was applied. The
 * screen therefore says "what changed", never "what will change" — the writes
 * already happened, and claiming otherwise would be the lie the design exists
 * to prevent.
 *
 * Disappeared rows are the case that matters most. A holding missing from a
 * statement is NOT deleted — it is flagged for review, because "absent from
 * this document" and "sold" are different facts and only you know which.
 */

interface ChangeSet {
  added: number;
  updated: number;
  disappeared: number;
  transactionsAdded: number;
  rows: { change: string; name: string; folio: string; old: number | null; new: number | null }[];
}

interface DocumentDto {
  id: string;
  kind: string | null;
  status: string;
  fileName: string | null;
  period: { from: string; to: string } | null;
  changeSet: ChangeSet | null;
  failure: { code: string; message: string | null } | null;
}

const CHANGE_LABEL: Record<string, { label: string; glyph: string; tone: string }> = {
  added: { label: 'new', glyph: '+', tone: 'var(--color-value-positive)' },
  updated: { label: 'changed', glyph: '±', tone: 'var(--color-label-secondary)' },
  disappeared: { label: 'not in this statement', glyph: '?', tone: 'var(--color-status-warning)' },
};

export function ChangeSetView({ documentId }: { documentId: string }) {
  const { data, isPending, error } = useQuery({
    queryKey: ['document', documentId],
    queryFn: () => api.get<DocumentDto>(`/documents/${documentId}`),
  });

  if (isPending) {
    return <p style={{ font: 'var(--type-subhead)', color: 'var(--color-label-secondary)' }}>Reading the statement…</p>;
  }
  if (error) return <p style={{ font: 'var(--type-subhead)' }}>{(error as Error).message}</p>;

  if (data.failure) {
    return (
      <p style={{ font: 'var(--type-subhead)', color: 'var(--color-status-warning)' }}>
        ⚠ {data.failure.message ?? data.failure.code}
      </p>
    );
  }

  const cs = data.changeSet;
  if (!cs) {
    return (
      <p style={{ font: 'var(--type-subhead)', color: 'var(--color-label-secondary)' }}>
        Status: {data.status}. Nothing has been applied yet.
      </p>
    );
  }

  const counts = [
    cs.added > 0 && `${cs.added} added`,
    cs.updated > 0 && `${cs.updated} updated`,
    cs.disappeared > 0 && `${cs.disappeared} to review`,
    cs.transactionsAdded > 0 && `${cs.transactionsAdded} transactions`,
  ].filter(Boolean) as string[];

  return (
    <div>
      <p style={{ font: 'var(--type-subhead)', margin: '0 0 var(--space-1)' }}>
        {counts.length > 0 ? counts.join(' · ') : 'Nothing changed — this statement matched what was already stored.'}
      </p>
      {data.period && (
        <p style={{ font: 'var(--type-caption)', color: 'var(--color-label-tertiary)', margin: '0 0 var(--space-4)' }}>
          Period {data.period.from} to {data.period.to}
        </p>
      )}

      {cs.rows.map((row, i) => {
        const meta = CHANGE_LABEL[row.change] ?? CHANGE_LABEL['updated']!;
        return (
          <div
            key={`${row.folio}-${row.name}-${i}`}
            style={{
              display: 'flex', gap: 'var(--space-3)', alignItems: 'baseline',
              padding: 'var(--space-2) 0', borderBottom: '1px solid var(--color-separator)',
            }}
          >
            {/* Glyph + word, never colour alone (DESIGN-SYSTEM §2.2). */}
            <span aria-hidden="true" style={{ color: meta.tone, font: 'var(--type-headline)', width: 16 }}>
              {meta.glyph}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', font: 'var(--type-subhead)', overflowWrap: 'anywhere' }}>
                {row.name}
              </span>
              <span style={{ font: 'var(--type-caption)', color: 'var(--color-label-tertiary)' }}>
                {row.folio} · {meta.label}
              </span>
            </span>
            <span style={{ textAlign: 'right' }}>
              {row.new !== null && <MoneyText value={{ amount: row.new, currency: 'INR' }} />}
              {row.change === 'updated' && row.old !== null && row.new !== null && (
                <span style={{ display: 'block', font: 'var(--type-caption)', color: 'var(--color-label-tertiary)' }}>
                  was <MoneyText value={{ amount: row.old, currency: 'INR' }} variant="compact" />
                </span>
              )}
              {row.change === 'disappeared' && row.old !== null && (
                <span style={{ display: 'block', font: 'var(--type-caption)', color: 'var(--color-label-tertiary)' }}>
                  last seen <MoneyText value={{ amount: row.old, currency: 'INR' }} variant="compact" />
                </span>
              )}
            </span>
          </div>
        );
      })}

      {cs.disappeared > 0 && (
        <p style={{ font: 'var(--type-footnote)', color: 'var(--color-status-warning)', marginTop: 'var(--space-3)' }}>
          Holdings marked “not in this statement” were kept, not deleted. Open one from Wealth to
          decide whether it was sold.
        </p>
      )}
    </div>
  );
}
